import { array } from "../../utils/arrayUtils";
import { boardFaceOutline } from "../Materials";
import type { FastenerRecord, SlotRecord } from "./plan-record";

/**
 * The geometry of a plan: every part is a box, and every question about
 * the layout is answered by the boxes. A slot's footprint is a rectangle
 * in the product's plan (turned by `angleDeg`) — with its mitered ends
 * cut off when the part lies flat, so a frame's rails meet along their
 * seams instead of lapping at the corners (`slotCorners`); its stance
 * says how tall the box stands from `zIn` — a board lying flat is as
 * tall as it is thick, on edge as tall as it is wide, on end as tall as
 * it is long. Rotation is about the vertical axis only, and there are
 * three stances: that constraint is what keeps this a plan editor and
 * not a modeler.
 *
 * A fastener is a head point and a direction — straight down from
 * above, or level along a heading at a height — and what it joins is
 * the first two boxes its shank passes through (`fastenerJoins`). The
 * crossing rule, the miter rule, and the end-butt rule
 * (`deriveFasteners`) are the editor's auto-place: one nail down
 * through every lap, one down through every mitered corner's seam, and
 * a pair through a face into every board end that butts against it.
 *
 * Pure: this module imports only the record's types and the board
 * face's outline, so the migration script and the tests can run it on
 * its own.
 */

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** A box's axis-aligned extent in the plan. */
export interface Extent {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

/** Float dust: a head on a face, an end against a face, count as touching. */
const EPS = 1e-6;

/** How far outside a footprint's edge a point still counts as on it:
 * the records keep three decimals, so a nail written down on a mitered
 * seam lands within a hair of the line, and a hair is not beside it. */
const EDGE_TOLERANCE_IN = 0.005;

// ---------------------------------------------------------------------
// Boxes

/** The footprint a slot's part presents, in inches: width across when it
 * lies flat, thickness across when it stands on edge. */
export function slotFaceWidthIn(slot: SlotRecord): number {
  return slot.onEdge ? slot.part.thicknessQ / 4 : slot.part.widthIn;
}

/** A slot part's local footprint before the slot's turn: across the
 * part, then along its length — which for a part stood on end is just
 * its cross-section (there is no length on the bench). */
export function slotFootprintIn(slot: SlotRecord): {
  wIn: number;
  hIn: number;
} {
  return slot.onEnd
    ? { wIn: slot.part.widthIn, hIn: slot.part.thicknessQ / 4 }
    : { wIn: slotFaceWidthIn(slot), hIn: slot.part.lengthIn };
}

/** How tall the part stands above `zIn`, from its stance. */
export function slotHeightIn(slot: SlotRecord): number {
  return slot.onEnd
    ? slot.part.lengthIn
    : slot.onEdge
      ? slot.part.widthIn
      : slot.part.thicknessQ / 4;
}

/** The top of the part above the bench. */
export function slotTopIn(slot: SlotRecord): number {
  return slot.zIn + slotHeightIn(slot);
}

/** A slot's axis-aligned footprint in product inches, for a part turned
 * by a right angle; a part turned otherwise gets the box around it. */
export function slotExtent(slot: SlotRecord): Extent {
  if (slot.angleDeg % 90 !== 0) return slotBounds(slot);
  const { wIn, hIn } = slotFootprintIn(slot);
  const across = slot.angleDeg % 180 !== 0;
  const w = across ? hIn : wIn;
  const h = across ? wIn : hIn;
  return {
    x0: slot.xIn - w / 2,
    y0: slot.yIn - h / 2,
    x1: slot.xIn + w / 2,
    y1: slot.yIn + h / 2,
  };
}

/** The axis-aligned box around a part, turned or not. */
export function slotBounds(slot: SlotRecord): Extent {
  const corners = slotCorners(slot);
  return {
    x0: Math.min(...corners.map((c) => c.x)),
    y0: Math.min(...corners.map((c) => c.y)),
    x1: Math.max(...corners.map((c) => c.x)),
    y1: Math.max(...corners.map((c) => c.y)),
  };
}

/** The unit vector a heading points along, in the slot-angle convention:
 * 0 runs down the sheet (+y), 90 to the left (−x). A part's length runs
 * along its `angleDeg` heading. */
export function headingVector(headingDeg: number): Point {
  const rad = (headingDeg * Math.PI) / 180;
  return { x: -Math.sin(rad), y: Math.cos(rad) };
}

/** Whether a part shows its miters in the plan: it lies flat, and an
 * end is cut off square. On edge or on end the cut runs down through
 * the part and the footprint is its full box. */
export function slotIsMitered(slot: SlotRecord): boolean {
  const ends = slot.part.ends;
  return (
    !slot.onEdge &&
    !slot.onEnd &&
    ends !== undefined &&
    (ends.left.kind === "mitered" || ends.right.kind === "mitered")
  );
}

/** A slot part's footprint outline in its own frame, before the turn:
 * across the part then along it, four corners, the left end's two
 * first — the same outline the finished piece draws. */
export function slotFaceOutline(slot: SlotRecord): ReadonlyArray<Point> {
  const { wIn, hIn } = slotFootprintIn(slot);
  if (slotIsMitered(slot)) {
    return boardFaceOutline(wIn, hIn, slot.part.ends);
  }
  return [
    { x: -wIn / 2, y: -hIn / 2 },
    { x: wIn / 2, y: -hIn / 2 },
    { x: wIn / 2, y: hIn / 2 },
    { x: -wIn / 2, y: hIn / 2 },
  ];
}

/** A slot part's footprint corners in product inches, honoring its
 * turn and its miters. Winds the same way as the face outline, so a
 * point is inside when it is on the left of every edge (`sideOf`). */
export function slotCorners(slot: SlotRecord): ReadonlyArray<Point> {
  const rad = (slot.angleDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return slotFaceOutline(slot).map(({ x: dx, y: dy }) => ({
    x: slot.xIn + dx * cos - dy * sin,
    y: slot.yIn + dx * sin + dy * cos,
  }));
}

/** Which side of the line through `a` and `b` a point lies on: positive
 * on the inside of an outline's edge, zero on the line. */
function sideOf(a: Point, b: Point, p: Point): number {
  return (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
}

const distance = (a: Point, b: Point): number =>
  Math.hypot(b.x - a.x, b.y - a.y);

/** Whether a plan point lies on the part's footprint (edges included). */
export function slotContains(
  slot: SlotRecord,
  xIn: number,
  yIn: number,
): boolean {
  const corners = slotCorners(slot);
  const p = { x: xIn, y: yIn };
  return corners.every((a, i) => {
    const b = corners[(i + 1) % corners.length];
    return sideOf(a, b, p) >= -EDGE_TOLERANCE_IN * distance(a, b);
  });
}

/** Whether two parts' boxes overlap in height (more than a touch). */
export function heightsOverlap(a: SlotRecord, b: SlotRecord): boolean {
  return Math.min(slotTopIn(a), slotTopIn(b)) - Math.max(a.zIn, b.zIn) > 0.01;
}

/** Whether `upper` sits right on `lower`: its underside at the other's top. */
export function restsOn(upper: SlotRecord, lower: SlotRecord): boolean {
  return Math.abs(upper.zIn - slotTopIn(lower)) < 0.01;
}

// ---------------------------------------------------------------------
// Polygons

/** Sutherland–Hodgman: the subject polygon clipped to a convex clip. */
function clipPolygon(
  subject: ReadonlyArray<Point>,
  clip: ReadonlyArray<Point>,
): ReadonlyArray<Point> {
  let output = [...subject];
  for (let i = 0; i < clip.length && output.length > 0; i++) {
    const a = clip[i];
    const b = clip[(i + 1) % clip.length];
    const input = output;
    output = [];
    const side = (p: Point) =>
      (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
    for (let j = 0; j < input.length; j++) {
      const current = input[j];
      const previous = input[(j + input.length - 1) % input.length];
      const currentIn = side(current) >= 0;
      const previousIn = side(previous) >= 0;
      if (currentIn !== previousIn) {
        const t = side(previous) / (side(previous) - side(current));
        output.push({
          x: previous.x + (current.x - previous.x) * t,
          y: previous.y + (current.y - previous.y) * t,
        });
      }
      if (currentIn) output.push(current);
    }
  }
  return output;
}

/** A polygon's area in square inches, whichever way it winds. */
export function polygonArea(poly: ReadonlyArray<Point>): number {
  let sum = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

function polygonCentroid(poly: ReadonlyArray<Point>): Point {
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const cross = a.x * b.y - b.x * a.y;
    area += cross;
    cx += (a.x + b.x) * cross;
    cy += (a.y + b.y) * cross;
  }
  return { x: cx / (3 * area), y: cy / (3 * area) };
}

/** The polygon where two slots' footprints lap, in product inches —
 * empty when they don't. */
export function slotOverlap(
  a: SlotRecord,
  b: SlotRecord,
): ReadonlyArray<Point> {
  const lap = clipPolygon(slotCorners(a), slotCorners(b));
  return lap.length < 3 ? [] : lap;
}

/** How much of the plan two parts share, in square inches. */
export function overlapArea(a: SlotRecord, b: SlotRecord): number {
  const lap = slotOverlap(a, b);
  return lap.length < 3 ? 0 : polygonArea(lap);
}

/** The footprint has to share real ground with another's to count as
 * lapping it — a grazing corner doesn't. */
const MIN_LAP_AREA = 0.05;

/** Whether two parts' footprints share ground. */
export function footprintsLap(a: SlotRecord, b: SlotRecord): boolean {
  return overlapArea(a, b) > MIN_LAP_AREA;
}

/**
 * Where a part would come to rest if it were let go where it lies: on
 * the highest top among the parts under it that it would otherwise sink
 * into, or on the bench. Parts entirely above it are not in the way.
 */
export function restingZ(
  slots: ReadonlyArray<SlotRecord>,
  slot: SlotRecord,
): number {
  const top = slotTopIn(slot);
  let z = 0;
  for (const other of slots) {
    if (other.id === slot.id || other.zIn >= top - 0.01) continue;
    if (!footprintsLap(slot, other)) continue;
    z = Math.max(z, slotTopIn(other));
  }
  return z;
}

// ---------------------------------------------------------------------
// Fasteners

export interface Vector3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * The unit direction a fastener's shank travels: straight down, or
 * level along its heading.
 */
export function fastenerDirection(fastener: FastenerRecord): Vector3 {
  if (!fastener.level) return { x: 0, y: 0, z: -1 };
  const { x, y } = headingVector(fastener.level.headingDeg);
  return { x, y, z: 0 };
}

/**
 * How high the head sits: a level fastener says; one driven down sits on
 * the top face of the uppermost part under it, or on the bench when
 * nothing is there.
 */
export function fastenerHeadZ(
  slots: ReadonlyArray<SlotRecord>,
  fastener: FastenerRecord,
): number {
  if (fastener.level) return fastener.level.zIn;
  let z = 0;
  for (const slot of slots) {
    if (slotContains(slot, fastener.xIn, fastener.yIn)) {
      z = Math.max(z, slotTopIn(slot));
    }
  }
  return z;
}

/** One box the shank passes through, and how far along it enters and
 * leaves, in inches from the head. */
export interface ShankHit {
  readonly slot: SlotRecord;
  readonly tIn: number;
  readonly tOut: number;
}

/** Where a ray from `origin` along `direction` passes through a part:
 * the footprint's edges as half-planes in the plan and a slab in
 * height, with the head allowed to sit right on a face. */
function rayThroughSlot(
  slot: SlotRecord,
  origin: Vector3,
  direction: Vector3,
): { tIn: number; tOut: number } | null {
  let tIn = -Infinity;
  let tOut = Infinity;
  const corners = slotCorners(slot);
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    const scale = distance(a, b);
    // Inside is the positive side; the ray's distance from the edge is
    // linear in t
    const at0 = sideOf(a, b, origin);
    const rate = (b.x - a.x) * direction.y - (b.y - a.y) * direction.x;
    if (Math.abs(rate) < EPS * scale) {
      if (at0 < -EDGE_TOLERANCE_IN * scale) return null;
      continue;
    }
    const t = -at0 / rate;
    if (rate > 0) tIn = Math.max(tIn, t);
    else tOut = Math.min(tOut, t);
  }
  const [lo, hi] = [slot.zIn, slotTopIn(slot)];
  if (Math.abs(direction.z) < EPS) {
    if (origin.z < lo - EPS || origin.z > hi + EPS) return null;
  } else {
    const a = (lo - origin.z) / direction.z;
    const b = (hi - origin.z) / direction.z;
    tIn = Math.max(tIn, Math.min(a, b));
    tOut = Math.min(tOut, Math.max(a, b));
  }
  if (tOut < Math.max(tIn, 0) + EPS) return null;
  return { tIn: Math.max(tIn, 0), tOut };
}

/**
 * The boxes a fastener's shank passes through, nearest the head first —
 * the head itself may sit on the first one's face.
 */
export function traceFastener(
  slots: ReadonlyArray<SlotRecord>,
  fastener: FastenerRecord,
): ReadonlyArray<ShankHit> {
  const origin = {
    x: fastener.xIn,
    y: fastener.yIn,
    z: fastenerHeadZ(slots, fastener),
  };
  const direction = fastenerDirection(fastener);
  const hits: ShankHit[] = [];
  for (const slot of slots) {
    const hit = rayThroughSlot(slot, origin, direction);
    if (hit) hits.push({ slot, ...hit });
  }
  return hits.sort((a, b) => a.tIn - b.tIn || a.tOut - b.tOut);
}

/**
 * The two parts a fastener joins — the first two its shank passes
 * through, the one its head is in first — or null when it passes
 * through fewer than two, which no fastener can do anything about.
 */
export function fastenerJoins(
  slots: ReadonlyArray<SlotRecord>,
  fastener: FastenerRecord,
): readonly [string, string] | null {
  const hits = traceFastener(slots, fastener);
  return hits.length < 2 ? null : [hits[0].slot.id, hits[1].slot.id];
}

// ---------------------------------------------------------------------
// Auto-place

/** Two lapping parts get fasteners at the lap — enough bite to matter,
 * so grazing corners don't. */
const MIN_OVERLAP_IN = 1;

/**
 * How far apart fasteners sit along a long joint. A crossing (a slat
 * over a rail) is smaller than this in both directions and gets exactly
 * one fastener at its center, the way a pallet does; a long parallel
 * seam — a cleat under a shelf, an apron along a table top — takes a
 * row of them, because one screw in a four-foot joint holds nothing.
 * A big two-dimensional lap (a doubled sheet top) takes a grid.
 */
const FASTENER_SPACING_IN = 16;

/** Fastener positions along one axis of an overlap: evenly spaced,
 * centered, one per FASTENER_SPACING_IN (rounded), never fewer than 1. */
function fastenerRun(from: number, to: number): ReadonlyArray<number> {
  const span = to - from;
  const count = Math.max(1, Math.round(span / FASTENER_SPACING_IN));
  return array(count).map((_, i) => from + (span * (i + 0.5)) / count);
}

/** How a board end is nailed: one nail across anything narrower than
 * this, and from there one per END_NAIL_SPACING_IN with two at least,
 * so a 4" board end takes two (its width, or its height on edge). */
const END_NAIL_SINGLE_BELOW_IN = 2.5;
const END_NAIL_SPACING_IN = 4;

/** Nail positions across one dimension of a board end: centered when
 * it's narrow, else evenly spread, two at least. */
function endRun(from: number, to: number): ReadonlyArray<number> {
  const span = to - from;
  const count =
    span < END_NAIL_SINGLE_BELOW_IN
      ? 1
      : Math.max(2, Math.round(span / END_NAIL_SPACING_IN));
  return array(count).map((_, i) => from + (span * (i + 0.5)) / count);
}

/** The short side of a slot's footprint — the bite-limiting dimension. */
function slotShortSideIn(slot: SlotRecord): number {
  const { wIn, hIn } = slotFootprintIn(slot);
  return Math.min(wIn, hIn);
}

/**
 * The crossing rule: one fastener down through every lap where a part
 * rests on another. A crossing gets one at its center, a long seam a
 * row, a big lap a grid (FASTENER_SPACING_IN); a grazing corner with
 * less than MIN_OVERLAP_IN of bite gets none.
 */
function deriveLapFasteners(
  slots: ReadonlyArray<SlotRecord>,
): ReadonlyArray<FastenerRecord> {
  const fasteners: FastenerRecord[] = [];
  for (const lower of slots) {
    for (const upper of slots) {
      if (upper === lower || !restsOn(upper, lower)) continue;
      // A pair with a turned member (the hex frame's rails) or a
      // mitered one (a roof over a gable) laps in a skewed polygon, not
      // an axis-aligned box: clip the two outlines against each other
      // and put one fastener at the lap's centroid. The bite bar
      // carries over from the square rule — the product of the per-axis
      // requirements, relaxed a step further because an angled lap
      // spreads the same bite over a slanted seam.
      if (
        lower.angleDeg % 90 !== 0 ||
        upper.angleDeg % 90 !== 0 ||
        slotIsMitered(lower) ||
        slotIsMitered(upper)
      ) {
        const lap = slotOverlap(lower, upper);
        if (lap.length < 3) continue;
        const needArea =
          Math.min(MIN_OVERLAP_IN, 0.75 * slotShortSideIn(lower)) *
          Math.min(MIN_OVERLAP_IN, 0.75 * slotShortSideIn(upper)) *
          0.75;
        if (polygonArea(lap) < needArea) continue;
        const at = polygonCentroid(lap);
        fasteners.push({ xIn: at.x, yIn: at.y });
        continue;
      }
      const a = slotExtent(lower);
      const b = slotExtent(upper);
      const x0 = Math.max(a.x0, b.x0);
      const y0 = Math.max(a.y0, b.y0);
      const x1 = Math.min(a.x1, b.x1);
      const y1 = Math.min(a.y1, b.y1);
      // The bite required per axis relaxes for thin parts: a pallet slat
      // crossing a rail stood on edge covers the rail's whole 3/4"
      // thickness, and full contact is all the bite there is to have.
      const needX = Math.min(
        MIN_OVERLAP_IN,
        0.75 * Math.min(a.x1 - a.x0, b.x1 - b.x0),
      );
      const needY = Math.min(
        MIN_OVERLAP_IN,
        0.75 * Math.min(a.y1 - a.y0, b.y1 - b.y0),
      );
      if (x1 - x0 < needX || y1 - y0 < needY) continue;
      for (const xIn of fastenerRun(x0, x1)) {
        for (const yIn of fastenerRun(y0, y1)) {
          fasteners.push({ xIn, yIn });
        }
      }
    }
  }
  return fasteners;
}

/** A mitered corner's seam has to be this long to take a nail: two
 * miters that merely graze at a point aren't a joint. */
const MIN_SEAM_IN = 0.25;

/** The mitered end edges of a part's footprint: the left end is the
 * outline's first edge, the right end its third. */
function miteredEndEdges(
  slot: SlotRecord,
): ReadonlyArray<readonly [Point, Point]> {
  if (!slotIsMitered(slot)) return [];
  const c = slotCorners(slot);
  const ends = slot.part.ends!;
  const edges: Array<readonly [Point, Point]> = [];
  if (ends.left.kind === "mitered") edges.push([c[0], c[1]]);
  if (ends.right.kind === "mitered") edges.push([c[2], c[3]]);
  return edges;
}

/** Where two segments lie along one line and overlap: the midpoint of
 * the overlap, or null when they don't. */
function seamBetween(
  [p, q]: readonly [Point, Point],
  [r, s]: readonly [Point, Point],
): Point | null {
  const length = distance(p, q);
  if (length < EPS) return null;
  const off = (point: Point) => Math.abs(sideOf(p, q, point)) / length;
  if (off(r) > 1e-3 || off(s) > 1e-3) return null;
  const along = (point: Point) =>
    ((point.x - p.x) * (q.x - p.x) + (point.y - p.y) * (q.y - p.y)) /
    (length * length);
  const t0 = Math.max(0, Math.min(along(r), along(s)));
  const t1 = Math.min(1, Math.max(along(r), along(s)));
  if ((t1 - t0) * length < MIN_SEAM_IN) return null;
  const t = (t0 + t1) / 2;
  return { x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t };
}

/**
 * The miter rule: where two parts' mitered ends meet along a seam at
 * the same height — a frame's corner — one nail goes down through the
 * middle of the seam, into both halves at once.
 */
function deriveMiterFasteners(
  slots: ReadonlyArray<SlotRecord>,
): ReadonlyArray<FastenerRecord> {
  const fasteners: FastenerRecord[] = [];
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const a = slots[i];
      const b = slots[j];
      if (!heightsOverlap(a, b)) continue;
      for (const edgeA of miteredEndEdges(a)) {
        for (const edgeB of miteredEndEdges(b)) {
          const seam = seamBetween(edgeA, edgeB);
          if (seam) fasteners.push({ xIn: tidy(seam.x), yIn: tidy(seam.y) });
        }
      }
    }
  }
  return fasteners;
}

/** How far past a board's end the probe reaches for the part it butts. */
const END_PROBE_IN = 0.05;

/** A position with the float dust of a turn and a probe brushed off. */
function tidy(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/**
 * The end-butt rule: where a board's end butts against the face of
 * another part standing beside it, nails go level through that part's
 * far face into the end grain — two across a 4" end (spread over its
 * width lying flat, over its height on edge), one across a thin one.
 * A part stood on end has no end to butt.
 */
function deriveEndFasteners(
  slots: ReadonlyArray<SlotRecord>,
): ReadonlyArray<FastenerRecord> {
  const fasteners: FastenerRecord[] = [];
  for (const part of slots) {
    if (part.onEnd) continue;
    const along = headingVector(part.angleDeg);
    const across = { x: -along.y, y: along.x };
    const { wIn, hIn } = slotFootprintIn(part);
    for (const end of [1, -1] as const) {
      const center = {
        x: part.xIn + (end * hIn * along.x) / 2,
        y: part.yIn + (end * hIn * along.y) / 2,
      };
      const probe = (offset: number, reach: number) => ({
        x: center.x + offset * across.x + end * reach * along.x,
        y: center.y + offset * across.y + end * reach * along.y,
      });
      // The whole end has to be backed: the middle and both corners of
      // the end face, just past it, all inside one part standing beside
      const inset = Math.max(0, wIn / 2 - END_PROBE_IN);
      const backing = slots.find(
        (other) =>
          other !== part &&
          heightsOverlap(part, other) &&
          [0, inset, -inset].every((offset) => {
            const p = probe(offset, END_PROBE_IN);
            return slotContains(other, p.x, p.y);
          }),
      );
      if (!backing) continue;
      // The nail comes back toward the end: its heading is the reverse
      // of the direction the end faces
      const headingDeg = end === 1 ? part.angleDeg + 180 : part.angleDeg;
      const z0 = Math.max(part.zIn, backing.zIn);
      const z1 = Math.min(slotTopIn(part), slotTopIn(backing));
      for (const offset of endRun(-wIn / 2, wIn / 2)) {
        // Head on the backing part's far face: cast from just past the
        // end, away from it, and take where the ray leaves the backing
        const from = probe(offset, END_PROBE_IN);
        const exit = rayThroughSlot(
          backing,
          { x: from.x, y: from.y, z: (z0 + z1) / 2 },
          { x: end * along.x, y: end * along.y, z: 0 },
        );
        if (!exit) continue;
        const head = probe(offset, END_PROBE_IN + exit.tOut);
        for (const zIn of endRun(z0, z1)) {
          fasteners.push({
            xIn: tidy(head.x),
            yIn: tidy(head.y),
            level: {
              headingDeg: ((headingDeg % 360) + 360) % 360,
              zIn: tidy(zIn),
            },
          });
        }
      }
    }
  }
  return fasteners;
}

/**
 * The fasteners the rules put on a layout — the editor's auto-place:
 * the crossing rule's nails down through every lap, the miter rule's
 * down through every mitered corner, then the end-butt rule's level
 * nails into every backed board end.
 */
export function deriveFasteners(
  slots: ReadonlyArray<SlotRecord>,
): ReadonlyArray<FastenerRecord> {
  return [
    ...deriveLapFasteners(slots),
    ...deriveMiterFasteners(slots),
    ...deriveEndFasteners(slots),
  ];
}
